// src/components/admin/MailSettingsEditor.js
//
// 問い合わせのメール通知先を設定する。
//
// 【保存先】
//   server_secrets/mail
//   settings/config ではない点が重要。
//   settings は allow read: if true なので、未ログインでも読める。
//   運営個人のメールアドレスを置く場所として不適切。
//
// 【書き込み権限】
//   firestore.rules の server_secrets は allow write: if isOwner()。
//   つまりオーナー以外の運営は保存できない（読み取りは isAdmin() で可能）。
//   これは LINE通知トークンを守るための制限で、意図的にそのままにしている。
//   保存に失敗したときは、その旨を画面で案内する。
//
// 【パスワードについて】
//   Gmailのアプリパスワードはここには保存しない。
//   Secret Manager（firebase functions:secrets:set SMTP_PASS）に入れる。
//   Firestore に置くと、管理者なら誰でも読めてしまうため。
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';

const MailSettingsEditor = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [from, setFrom] = useState('');
    const [toText, setToText] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        (async () => {
            setIsLoading(true);
            try {
                const snap = await getDoc(doc(db, 'server_secrets', 'mail'));
                if (snap.exists()) {
                    const d = snap.data();
                    setFrom(d.from || '');
                    setToText(Array.isArray(d.to) ? d.to.join('\n') : '');
                }
            } catch (e) {
                console.error(e);
                alert('設定の読み込みに失敗しました。運営メンバーの権限をご確認ください。');
            } finally {
                setIsLoading(false);
            }
        })();
    }, [isOpen]);

    const parseTo = () => toText.split('\n').map(s => s.trim()).filter(Boolean);

    const handleSave = async () => {
        const to = parseTo();
        if (!from.trim()) { alert('送信元アドレスを入力してください'); return; }
        if (to.length === 0) { alert('宛先を1件以上入力してください'); return; }

        setIsSaving(true);
        try {
            await setDoc(doc(db, 'server_secrets', 'mail'), {
                from: from.trim(),
                to,
                updatedAt: new Date().toISOString()
            }, { merge: true });
            alert('✅ 保存しました');
        } catch (e) {
            console.error(e);
            alert('❌ 保存できませんでした。\n\nこの設定はオーナーのみ変更できます。\nオーナー以外の場合は、Firebaseコンソールから編集してください。');
        } finally {
            setIsSaving(false);
        }
    };

    const handleTest = async () => {
        setIsTesting(true);
        try {
            const fns = getFunctions(getApp(), 'asia-northeast1');
            const res = await httpsCallable(fns, 'sendTestMail')({});
            const list = (res.data && res.data.to) ? res.data.to.join('\n') : '';
            alert(`✅ テストメールを送信しました。\n\n${list}\n\n数分待っても届かない場合は迷惑メールフォルダもご確認ください。`);
        } catch (e) {
            console.error(e);
            alert(`❌ 送信できませんでした。\n\n${e.message || ''}`);
        } finally {
            setIsTesting(false);
        }
    };

    return (
        <div style={{ marginBottom: '20px', border: '1px solid #ddd', borderRadius: '8px', background: 'white', overflow: 'hidden' }}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    padding: '15px', background: '#ede7f6', cursor: 'pointer', display: 'flex',
                    justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold', color: '#4527a0'
                }}
            >
                <span>📧 問い合わせのメール通知</span>
                <span>{isOpen ? '▲ 閉じる' : '▼ 開く'}</span>
            </div>

            {isOpen && (
                <div style={{ padding: '15px' }}>
                    {isLoading ? (
                        <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>読み込み中...</div>
                    ) : (
                        <>
                            <div style={{
                                background: '#f3f8ff', border: '1px solid #bbdefb', borderRadius: '8px',
                                padding: '12px', marginBottom: '15px', fontSize: '11px', color: '#1565c0', lineHeight: '1.8'
                            }}>
                                会員から新しい問い合わせが届いたときに、下記のアドレスへ通知メールを送ります。<br />
                                同じ相手が連続で送っても、運営が既読にするまでは1通だけ届きます。<br />
                                <b>メール本文に会員のメッセージ内容は含まれません</b>（内容は問合せタブで確認してください）。
                            </div>

                            <div style={{ marginBottom: '12px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', marginBottom: '4px', color: '#555' }}>
                                    送信元アドレス（Gmail）
                                </label>
                                <input
                                    value={from}
                                    onChange={e => setFrom(e.target.value)}
                                    placeholder="例: revengers.app@gmail.com"
                                    style={{ width: '100%', padding: '9px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }}
                                />
                                <div style={{ fontSize: '10px', color: '#999', marginTop: '4px', lineHeight: '1.6' }}>
                                    このアカウントで発行した「アプリパスワード」を、別途 Secret Manager に登録しておく必要があります。
                                </div>
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', marginBottom: '4px', color: '#555' }}>
                                    宛先（1行に1つ）
                                </label>
                                <textarea
                                    value={toText}
                                    onChange={e => setToText(e.target.value)}
                                    rows={4}
                                    placeholder={'例:\nyamada@example.com\nsuzuki@example.com'}
                                    style={{
                                        width: '100%', padding: '9px', boxSizing: 'border-box', border: '1px solid #ccc',
                                        borderRadius: '4px', fontSize: '13px', resize: 'vertical', fontFamily: 'inherit'
                                    }}
                                />
                                <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>
                                    現在 {parseTo().length} 件
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="btn btn-primary"
                                    style={{ flex: 2, padding: '12px' }}
                                >
                                    {isSaving ? '保存中...' : '📧 設定を保存'}
                                </button>
                                <button
                                    onClick={handleTest}
                                    disabled={isTesting}
                                    className="btn"
                                    style={{ flex: 1, padding: '12px', background: '#f5f5f5', border: '1px solid #ccc', fontSize: '13px' }}
                                >
                                    {isTesting ? '送信中...' : 'テスト送信'}
                                </button>
                            </div>

                            <div style={{ fontSize: '10px', color: '#d32f2f', marginTop: '10px', lineHeight: '1.7' }}>
                                ※ この設定を保存できるのはオーナーのみです。<br />
                                　 他の運営メンバーは閲覧のみ可能です（変更が必要な場合はオーナーへ）。
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default MailSettingsEditor;
